import i18n from "@/lib/i18n";
import type { MaritimeForecastComponent } from "@/lib/api";

export type MaritimeBroadcastPhenomenaFormatConfig = {
  locale: "nl" | "en";
  phenomena: string[];
  sep: string;
  phenomenaInlineKeys: Record<string, string>;
};

export const getTextForLocale = (
  entries: MaritimeForecastComponent[],
  phenomenon: string,
  locale: "nl" | "en",
): string | null => {
  const entry = entries.find((e) => e.phenomenon === phenomenon);
  if (!entry || entry.type !== "text") return null;
  return entry.message[locale] ?? entry.message.nl ?? entry.message.en ?? null;
};

const extractInlineLabel = (inlineTemplate: string): string | null => {
  if (inlineTemplate === "{{value}}") return null;
  const label = inlineTemplate.replace(/\s*\{\{value\}\}\s*$/, "").trim();
  return label || null;
};

const phenomenonTextAlreadyLabeled = (
  text: string,
  label: string | null,
  locale: "nl" | "en",
): boolean => {
  if (!label) return false;

  const normalized = text.toLocaleLowerCase(locale);
  const normalizedLabel = label.toLocaleLowerCase(locale);

  return (
    normalized.startsWith(`${normalizedLabel} `) ||
    normalized.endsWith(` ${normalizedLabel}`) ||
    normalized === normalizedLabel
  );
};

/** Applies per-phenomenon inline prefix templates for broadcast sentence building. */
export const formatBroadcastPhenomenonText = (
  entries: MaritimeForecastComponent[],
  phenomenon: string,
  locale: "nl" | "en",
  phenomenaInlineKeys: Record<string, string>,
): string | null => {
  // i18next's typed `t` rejects dynamic (runtime) keys; the inline phenomenon keys are
  // only known at runtime, so resolve them through a string-keyed signature.
  const t = i18n.t as unknown as (key: string, options?: Record<string, unknown>) => string;

  const text = getTextForLocale(entries, phenomenon, locale);
  if (!text) return null;

  const inlineKey = phenomenaInlineKeys[phenomenon];
  if (!inlineKey) return text;

  const label = extractInlineLabel(t(inlineKey, { lng: locale }));
  if (phenomenonTextAlreadyLabeled(text, label, locale)) return text;

  return t(inlineKey, { value: text, lng: locale });
};

/**
 * Joins only phenomena that are present in the payload (missing keys are omitted).
 */
export const buildAreaPhenomenaText = (
  areaEntries: MaritimeForecastComponent[],
  { locale, phenomena, sep, phenomenaInlineKeys }: MaritimeBroadcastPhenomenaFormatConfig,
): string =>
  phenomena
    .map((ph) =>
      formatBroadcastPhenomenonText(areaEntries, ph, locale, phenomenaInlineKeys),
    )
    .filter((t): t is string => t !== null)
    .join(sep);
